// The DOM half of the prep editor's keyboard: bold, bullets, highlight, and the
// bullet moves (indent, outdent, backspace, enter). What to do is decided by the
// pure functions in prepEdit.ts; this file only carries it out on the live
// elements and keeps the caret where the person left it. Removing a node resets
// any range inside it, so every move saves the caret first and sets it again.

import { backspaceAtStart, enterInBullet, tabInBullet, type BulletContext } from "./prepEdit";

type Caret = { node: Node; offset: number };

function selection(): Selection | null {
  return window.getSelection();
}

function fireInput(body: HTMLElement): void {
  body.dispatchEvent(new Event("input", { bubbles: true }));
}

function setCaret(c: Caret): void {
  const sel = selection();
  if (!sel) return;
  const r = document.createRange();
  r.setStart(c.node, c.offset);
  r.collapse(true);
  sel.removeAllRanges();
  sel.addRange(r);
}

function currentCaret(): Caret | null {
  const sel = selection();
  if (!sel || !sel.anchorNode) return null;
  return { node: sel.anchorNode, offset: sel.anchorOffset };
}

/** The bullet the caret is in, if it is inside `body`. */
export function bulletAt(body: HTMLElement, node: Node | null): HTMLLIElement | null {
  if (!node) return null;
  const el = node instanceof Element ? node : node.parentElement;
  const li = el?.closest("li") ?? null;
  return li && body.contains(li) ? (li as HTMLLIElement) : null;
}

function ownText(li: Element): string {
  let text = "";
  for (const n of Array.from(li.childNodes)) {
    if (n instanceof Element && n.tagName === "UL") continue;
    text += n.textContent ?? "";
  }
  return text;
}

function contextOf(li: HTMLLIElement): BulletContext {
  const prev = li.previousElementSibling;
  return {
    nested: li.parentElement?.parentElement?.tagName === "LI",
    empty: (li.textContent ?? "").trim() === "",
    hasPrev: !!prev && prev.tagName === "LI",
  };
}

/** Whether the caret is at the very start of the bullet's own text. */
function atStartOf(li: HTMLLIElement): boolean {
  const sel = selection();
  if (!sel || !sel.isCollapsed || !sel.anchorNode) return false;
  const r = document.createRange();
  r.setStart(li, 0);
  r.setEnd(sel.anchorNode, sel.anchorOffset);
  const frag = r.cloneContents();
  return (frag.textContent ?? "") === "" && !frag.querySelector("ul");
}

function nestedList(li: HTMLLIElement, create: boolean): HTMLUListElement | null {
  const last = li.lastElementChild;
  if (last && last.tagName === "UL") return last as HTMLUListElement;
  if (!create) return null;
  const ul = document.createElement("ul");
  li.appendChild(ul);
  return ul;
}

/** The last bullet shown inside `li`, going down through nested lists. */
function deepestLast(li: HTMLLIElement): HTMLLIElement {
  let t = li;
  for (;;) {
    const ul = t.lastElementChild;
    if (ul && ul.tagName === "UL" && ul.lastElementChild) t = ul.lastElementChild as HTMLLIElement;
    else return t;
  }
}

/** The caret position at the end of a bullet's own text, before any nested list. */
function endOfOwn(li: HTMLLIElement): Caret {
  const own = Array.from(li.childNodes).filter((n) => !(n instanceof Element && n.tagName === "UL"));
  let last: Node | undefined = own[own.length - 1];
  if (!last) return { node: li, offset: 0 };
  if (last instanceof Element && last.tagName === "BR") return { node: li, offset: Array.prototype.indexOf.call(li.childNodes, last) };
  while (last && last.lastChild) last = last.lastChild;
  if (last && last.nodeType === Node.TEXT_NODE) return { node: last, offset: (last as Text).length };
  return { node: li, offset: Array.prototype.indexOf.call(li.childNodes, own[own.length - 1]) + 1 };
}

function indent(body: HTMLElement, li: HTMLLIElement): void {
  const prev = li.previousElementSibling;
  if (!prev || prev.tagName !== "LI") return;
  const caret = currentCaret();
  nestedList(prev as HTMLLIElement, true)?.appendChild(li);
  if (caret) setCaret(caret);
  fireInput(body);
}

function outdent(body: HTMLElement, li: HTMLLIElement): void {
  const parentUl = li.parentElement;
  const parentLi = parentUl?.parentElement;
  if (!parentUl || !parentLi || parentLi.tagName !== "LI") return;
  const caret = currentCaret();
  const followers: Element[] = [];
  for (let n = li.nextElementSibling; n; n = n.nextElementSibling) followers.push(n);
  if (followers.length) {
    const ul = nestedList(li, true);
    for (const f of followers) ul?.appendChild(f);
  }
  parentLi.after(li);
  if (parentUl.children.length === 0) parentUl.remove();
  if (caret) setCaret(caret);
  fireInput(body);
}

function removeBullet(body: HTMLElement, li: HTMLLIElement): void {
  const prev = li.previousElementSibling as HTMLLIElement;
  const caret = endOfOwn(deepestLast(prev));
  li.remove();
  setCaret(caret);
  fireInput(body);
}

function mergeBullet(body: HTMLElement, li: HTMLLIElement): void {
  const prev = li.previousElementSibling as HTMLLIElement;
  const target = deepestLast(prev);
  // An empty target keeps a break for the caret; real text does not need it.
  const caret = endOfOwn(target);
  const own = Array.from(li.childNodes).filter((n) => !(n instanceof Element && n.tagName === "UL"));
  const lone = own.length === 1 && own[0] instanceof Element && own[0].tagName === "BR";
  const nested = nestedList(li, false);
  if (!lone) for (const n of own) target.appendChild(n);
  if (nested) target.appendChild(nested);
  li.remove();
  // A target that was only a break now holds text, so the break goes.
  for (const br of Array.from(target.children)) if (br.tagName === "BR" && ownText(target) !== "") br.remove();
  setCaret(caret.node.isConnected ? caret : { node: target, offset: target.childNodes.length });
  fireInput(body);
}

// ---- highlight ----------------------------------------------------------------------

function unwrap(el: Element): void {
  const parent = el.parentNode;
  if (!parent) return;
  while (el.firstChild) parent.insertBefore(el.firstChild, el);
  parent.removeChild(el);
}

function markOf(node: Node, body: HTMLElement): HTMLElement | null {
  const el = node instanceof Element ? node : node.parentElement;
  const m = el?.closest("mark") ?? null;
  return m && body.contains(m) ? (m as HTMLElement) : null;
}

/** Wraps the selected text in `mark`, or takes the mark off if the selection is
 * already highlighted. */
export function toggleHighlight(body: HTMLElement): void {
  const sel = selection();
  if (!sel || sel.rangeCount === 0) return;
  const range = sel.getRangeAt(0);
  if (!body.contains(range.commonAncestorContainer)) return;

  if (range.collapsed) {
    const m = markOf(range.startContainer, body);
    if (!m) return;
    const c = { node: range.startContainer, offset: range.startOffset };
    unwrap(m);
    if (c.node.isConnected) setCaret(c);
    fireInput(body);
    return;
  }

  const root = range.commonAncestorContainer;
  const texts: Text[] = [];
  if (root.nodeType === Node.TEXT_NODE) texts.push(root as Text);
  else {
    const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
    for (let n = walker.nextNode(); n; n = walker.nextNode()) {
      const t = n as Text;
      if (!range.intersectsNode(t)) continue;
      const from = t === range.startContainer ? range.startOffset : 0;
      const to = t === range.endContainer ? range.endOffset : t.length;
      if (to > from && t.data.slice(from, to).trim() !== "") texts.push(t);
    }
  }
  if (texts.length === 0) return;

  const saved = { sc: range.startContainer, so: range.startOffset, ec: range.endContainer, eo: range.endOffset };
  if (texts.every((t) => markOf(t, body))) {
    const marks = new Set(texts.map((t) => markOf(t, body) as HTMLElement));
    for (const m of marks) unwrap(m);
    if (saved.sc.isConnected && saved.ec.isConnected) {
      const r = document.createRange();
      r.setStart(saved.sc, saved.so);
      r.setEnd(saved.ec, saved.eo);
      sel.removeAllRanges();
      sel.addRange(r);
    }
    fireInput(body);
    return;
  }

  let first: Node | null = null;
  let last: Node | null = null;
  for (const t of texts) {
    if (markOf(t, body)) {
      first = first ?? t;
      last = t;
      continue;
    }
    const from = t === saved.sc ? saved.so : 0;
    const to = t === saved.ec ? saved.eo : t.length;
    if (to <= from) continue;
    if (to < t.length) t.splitText(to);
    const mid = from > 0 ? t.splitText(from) : t;
    const m = document.createElement("mark");
    mid.parentNode?.insertBefore(m, mid);
    m.appendChild(mid);
    first = first ?? mid;
    last = mid;
  }
  if (first && last) {
    const r = document.createRange();
    r.setStart(first, 0);
    r.setEnd(last, last.nodeType === Node.TEXT_NODE ? (last as Text).length : last.childNodes.length);
    sel.removeAllRanges();
    sel.addRange(r);
  }
  fireInput(body);
}

// ---- commands ----------------------------------------------------------------------

function exec(command: string): void {
  try {
    document.execCommand(command, false);
  } catch {
    // An editor with no command support keeps the text as it was.
  }
}

/** Pastes plain text at the caret, one line at a time so a list gets a bullet per line. */
export function pastePlain(body: HTMLElement, text: string): void {
  const lines = text.split(/\r?\n/).map((l) => l.trim()).filter(Boolean);
  if (lines.length === 0) return;
  lines.forEach((line, i) => {
    if (i > 0) exec("insertParagraph");
    try {
      document.execCommand("insertText", false, line);
    } catch {
      // Falls through with nothing inserted.
    }
  });
  fireInput(body);
}

/** Handles the editor's shortcuts. Returns true when the event was taken. */
export function handleKey(e: KeyboardEvent, body: HTMLElement): boolean {
  const mod = e.ctrlKey || e.metaKey;
  const key = e.key;

  if (mod && !e.altKey) {
    const lower = key.toLowerCase();
    if (lower === "b" && !e.shiftKey) {
      e.preventDefault();
      try {
        document.execCommand("styleWithCSS", false, "false");
      } catch {
        // Not every browser has it.
      }
      exec("bold");
      return true;
    }
    if (key === "8" || key === "*") {
      e.preventDefault();
      exec("insertUnorderedList");
      return true;
    }
    if (lower === "h") {
      e.preventDefault();
      toggleHighlight(body);
      return true;
    }
    return false;
  }

  const sel = selection();
  if (!sel || !sel.isCollapsed) return false;
  const li = bulletAt(body, sel.anchorNode);
  if (!li) return false;

  if (key === "Tab") {
    e.preventDefault();
    const action = tabInBullet(contextOf(li), e.shiftKey);
    if (action === "indent") indent(body, li);
    else if (action === "outdent") outdent(body, li);
    return true;
  }
  if (key === "Backspace" && !e.shiftKey && atStartOf(li)) {
    const action = backspaceAtStart(contextOf(li));
    e.preventDefault();
    if (action === "outdent") outdent(body, li);
    else if (action === "remove") removeBullet(body, li);
    else if (action === "merge") mergeBullet(body, li);
    return true;
  }
  if (key === "Enter" && !e.shiftKey) {
    const action = enterInBullet(contextOf(li));
    if (action === "default") return false;
    e.preventDefault();
    if (action === "outdent") outdent(body, li);
    return true;
  }
  return false;
}

/**
 * Marks each table's header row and gives every other cell its column name,
 * which a phone shows above the value once the rows stack. Neither survives
 * sanitizeHtml, so it is set again after every paint and every keystroke.
 */
export function labelTables(body: HTMLElement): void {
  for (const t of Array.from(body.querySelectorAll("table"))) {
    const rows = Array.from(t.querySelectorAll("tr"));
    const head = rows[0] && rows[0].querySelector("td") === null ? rows[0] : null;
    const names = head ? Array.from(head.children).map((c) => (c.textContent ?? "").trim()) : [];
    if (head) head.className = "hrow";
    for (const r of rows) {
      if (r === head) continue;
      Array.from(r.children).forEach((c, i) => {
        if (names[i]) c.setAttribute("data-label", names[i]);
        else c.removeAttribute("data-label");
      });
    }
  }
}
